import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { Book, Me, ReviewItem } from "../api";
import { useAuth } from "../auth";
import { BookInfo, Card, ErrorNote, JobBadge, LegalBadges, PD_LABELS, Spinner, usd, useLoad } from "../ui";
import { EntitiesPanel } from "./EntitiesPanel";
import { HistoryPanel } from "./HistoryPanel";
import { ReviewQueue } from "./ReviewQueue";

type Tab = "overzicht" | "controleren" | "namen" | "geschiedenis";
const TABS: { id: Tab; label: string }[] = [
  { id: "overzicht", label: "Overzicht" },
  { id: "controleren", label: "Controleren" },
  { id: "namen", label: "Personages & namen" },
  { id: "geschiedenis", label: "Geschiedenis" },
];

/** Owner's workspace for one book: progress, review queue, entities and edit history. */
export function StudioPage({ me }: { me: Me }) {
  const { bookId = "" } = useParams();
  const { api } = useAuth();
  const book = useLoad(() => api.book(bookId), [api, bookId]);
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as Tab) || "overzicht";

  const processing = book.data?.job_status === "queued" || book.data?.job_status === "running";
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(book.reload, 4000);
    return () => clearInterval(t);
  }, [processing, book.reload]);

  if (book.error) return <ErrorNote error={book.error} />;
  if (!book.data) return <Spinner />;
  const b = book.data;
  if (b.owner_id !== me.id) return <ErrorNote error="Alleen de eigenaar kan dit boek beheren." />;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>{b.title}</h1>
          <p className="muted">
            {b.authors.join(", ")}{" "}
            <JobBadge status={b.job_status} stage={b.job_stage} notBefore={b.job_not_before} />
          </p>
          <LegalBadges book={b} />
        </div>
        {b.last_chapter_index !== null && (
          <a className="btn" href={`#/books/${b.id}/wiki`}>
            Open wiki
          </a>
        )}
      </div>
      <div className="tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={`tab ${tab === t.id ? "tab-active" : ""}`}
            onClick={() => setParams({ tab: t.id }, { replace: true })}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "overzicht" && <Overview book={b} onChange={book.reload} />}
      {b.last_chapter_index === null && tab !== "overzicht" ? (
        <p className="muted">Beschikbaar zodra het boek is verwerkt.</p>
      ) : (
        <>
          {tab === "controleren" && <ReviewQueue book={b} />}
          {tab === "namen" && <EntitiesPanel book={b} />}
          {tab === "geschiedenis" && <HistoryPanel book={b} />}
        </>
      )}
    </div>
  );
}

/** Reading position for review: defaults to the reader's bookmark (no spoilers while reading). */
export function useReviewBookmark(book: Book): [number, (n: number) => void] {
  const first = book.first_chapter_index ?? 0;
  const last = book.last_chapter_index ?? 0;
  const [value, setValue] = useState<number>(() => {
    try {
      const v = localStorage.getItem(`fabula.bookmark.${book.id}`);
      return v === null ? last : Math.min(Math.max(Number(v), first), last);
    } catch {
      return last;
    }
  });
  return [value, setValue];
}

export function ReviewBookmark({
  book,
  value,
  onChange,
}: {
  book: Book;
  value: number;
  onChange: (n: number) => void;
}) {
  const first = book.first_chapter_index ?? 0;
  const last = book.last_chapter_index ?? 0;
  return (
    <label className="row review-bookmark">
      Controleren t/m
      <select value={value} onChange={(e) => onChange(Number(e.target.value))}>
        {Array.from({ length: last - first + 1 }, (_, i) => first + i).map((n) => (
          <option key={n} value={n}>
            H{n}
            {n === last ? " (alles)" : ""}
          </option>
        ))}
      </select>
      <span className="muted small">Lees je het boek nog? Kies je eigen hoofdstuk: geen spoilers.</span>
    </label>
  );
}

function Overview({ book, onChange }: { book: Book; onChange: () => void }) {
  const { api } = useAuth();
  const navigate = useNavigate();
  const jobs = useLoad(() => api.jobs(book.id), [api, book.id, book.job_status, book.job_stage]);
  const last = book.last_chapter_index;
  const items = useLoad(
    () => (last === null ? Promise.resolve([] as ReviewItem[]) : api.review(book.id, last)),
    [api, book.id, last],
  );
  const stats = useMemo(() => {
    const s: Record<string, Record<string, number>> = {};
    for (const i of items.data ?? []) {
      s[i.item_type] ??= {};
      s[i.item_type][i.status] = (s[i.item_type][i.status] ?? 0) + 1;
    }
    return s;
  }, [items.data]);
  const open = (items.data ?? []).filter((i) => i.status === "unverified" || i.status === "rejected").length;
  const total = items.data?.length ?? 0;
  const progress =
    book.job_progress_total && book.job_status === "running"
      ? Math.round((100 * (book.job_progress_done ?? 0)) / book.job_progress_total)
      : null;

  async function remove() {
    if (!window.confirm(`"${book.title}" en alle bijbehorende gegevens verwijderen?`)) return;
    try {
      await api.deleteBook(book.id);
      navigate("/");
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="stack">
      {progress !== null && (
        <Card>
          <h2>Bezig met verwerken</h2>
          <div className="progress" role="progressbar" aria-valuenow={progress} aria-valuemin={0}
               aria-valuemax={100}>
            <div className="progress-bar" style={{ width: `${progress}%` }} />
          </div>
          <p className="muted">
            Hoofdstuk {book.job_progress_done} van {book.job_progress_total} geanalyseerd.
          </p>
        </Card>
      )}
      {book.job_error && <p className="note">{book.job_error}</p>}
      {total > 0 && (
        <Card>
          <h2>Controle</h2>
          <p>
            {open === 0 ? (
              <>Alles is gecontroleerd of automatisch bevestigd. </>
            ) : (
              <>
                <strong>{open}</strong> van {total} items wachten op jouw oordeel (citaat niet of
                niet exact gevonden).{" "}
              </>
            )}
            <a href={`#/books/${book.id}/beheer?tab=controleren`}>Naar controleren</a>
          </p>
          <table className="stats">
            <thead>
              <tr>
                <th></th>
                <th className="num">zichtbaar</th>
                <th className="num">onzeker</th>
                <th className="num">niet gevonden</th>
                <th className="num">verborgen</th>
              </tr>
            </thead>
            <tbody>
              {(["fact", "relation", "event"] as const).map((t) => (
                <tr key={t}>
                  <td>{{ fact: "Feiten", relation: "Relaties", event: "Gebeurtenissen" }[t]}</td>
                  {(["verified", "unverified", "rejected", "hidden"] as const).map((s) => (
                    <td key={s} className="num">
                      {stats[t]?.[s] ?? 0}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <Card>
        <h2>Boekgegevens</h2>
        <BookInfo book={book} />
      </Card>
      <SharingCard book={book} onChange={onChange} />
      <SourceCard book={book} onChange={onChange} />
      <Card>
        <h2>Verwerkingen</h2>
        {!jobs.data ? (
          <Spinner />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Gestart</th>
                  <th>Status</th>
                  <th className="num">Kosten</th>
                  <th className="num">Budget</th>
                  <th>Opmerking</th>
                </tr>
              </thead>
              <tbody>
                {jobs.data.map((j) => (
                  <tr key={j.id}>
                    <td>{(j.started_at ?? j.created_at).slice(0, 16).replace("T", " ")}</td>
                    <td>
                      <JobBadge status={j.status} stage={j.stage} />
                      {!j.charge_credit && <span className="muted small"> (gratis)</span>}
                    </td>
                    <td className="num">{usd(j.spent_usd)}</td>
                    <td className="num">{usd(j.budget_usd)}</td>
                    <td className="small" title={j.error ?? undefined}>
                      {j.error && j.error.length > 140 ? `${j.error.slice(0, 140)}…` : (j.error ?? "")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="row" style={{ marginTop: "1rem" }}>
          <button className="btn btn-ghost" onClick={onChange}>
            Vernieuwen
          </button>
          <button className="btn btn-ghost btn-danger" onClick={() => void remove()}>
            Boek verwijderen
          </button>
        </div>
      </Card>
    </div>
  );
}

/** Publishing the wiki: other users can then look things up and read the story so far. */
function SharingCard({ book, onChange }: { book: Book; onChange: () => void }) {
  const { api } = useAuth();
  const isPublic = book.visibility === "public";
  const [basis, setBasis] = useState<"public_domain" | "licensed">(
    book.rights_basis === "licensed" ? "licensed" : "public_domain",
  );
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const pd = book.public_domain.status;

  async function share(visibility: "public" | "private") {
    setBusy(true);
    setError(null);
    setWarnings([]);
    try {
      const r = await api.setSharing(
        book.id,
        visibility === "public" ? { visibility, rights_basis: basis, confirmed } : { visibility },
      );
      setWarnings(r.warnings);
      setConfirmed(false);
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h2>Delen</h2>
      {isPublic ? (
        <>
          <p>
            Deze wiki is <b>openbaar</b>: iedereen met een account vindt hem bij{" "}
            <a href="#/openbaar">Openbare boeken</a> en kan opzoeken, het verhaal tot nu toe lezen
            en de wiki openen, steeds tot hun eigen boekenlegger. Alleen jij kunt beheren.
          </p>
          <button className="btn btn-ghost" disabled={busy} onClick={() => void share("private")}>
            Weer privé maken
          </button>
        </>
      ) : book.last_chapter_index === null ? (
        <p className="muted">Je kunt het boek delen zodra het verwerkt is.</p>
      ) : (
        <>
          <p className="muted small">
            Deel alleen boeken die vrij zijn van auteursrecht (publiek domein) of waarvoor je
            toestemming van de rechthebbende hebt. Anderen zien de feiten, nooit de boektekst of
            citaten, en niets over jou of je bestand.
          </p>
          <fieldset className="choices">
            <legend className="small">Grondslag</legend>
            <label className="check small">
              <input
                type="radio"
                name="basis"
                checked={basis === "public_domain"}
                onChange={() => setBasis("public_domain")}
              />
              Publiek domein{" "}
              <span className="muted">
                (controle: {pd ? PD_LABELS[pd] ?? pd : "nog niet gecontroleerd"})
              </span>
            </label>
            <label className="check small">
              <input
                type="radio"
                name="basis"
                checked={basis === "licensed"}
                onChange={() => setBasis("licensed")}
              />
              Ik heb toestemming (licentie) van de rechthebbende
            </label>
          </fieldset>
          <label className="check">
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
            <span>
              Ik verklaar dat ik dit boek mag delen op deze grondslag (zie de{" "}
              <a href="#/voorwaarden" target="_blank" rel="noreferrer">
                voorwaarden
              </a>
              ).
            </span>
          </label>
          <div>
            <button className="btn" disabled={busy || !confirmed} onClick={() => void share("public")}>
              Openbaar maken
            </button>
          </div>
        </>
      )}
      {warnings.length > 0 && (
        <ul className="findings">
          {warnings.map((w) => (
            <li key={w} className="small">{sharingWarning(w)}</li>
          ))}
        </ul>
      )}
      <ErrorNote error={error && sharingWarning(error)} />
    </Card>
  );
}

/** The API speaks English; the public-domain warning is the one an owner can run into. */
function sharingWarning(msg: string): string {
  if (msg.includes("public-domain check"))
    return "De automatische publiek-domeincontrole is niet positief. Controleer sterfjaar van de auteur en de vertaler; bij twijfel kan een beheerder het delen weigeren.";
  if (msg.includes("no chapters")) return "Het boek heeft nog geen hoofdstukken om te delen.";
  return msg;
}

/** Provenance, rights and data retention; re-offering the file to reload without LLM cost. */
function SourceCard({ book, onChange }: { book: Book; onChange: () => void }) {
  const { api } = useAuth();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const findings = book.provenance.findings ?? [];
  const pd = book.public_domain;
  const processing = book.job_status === "queued" || book.job_status === "running";

  async function checkPublicDomain() {
    setBusy(true);
    setError(null);
    try {
      await api.publicDomainCheckOwn(book.id);
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function reoffer(file: File | null) {
    if (!file) return;
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      await api.reofferSource(book.id, file);
      setMsg("Het bestand is aangeboden; het boek wordt opnieuw opgebouwd (zonder kosten).");
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h2>Bron, rechten en bewaren</h2>
      <dl className="facts-grid">
        <dt>Verklaring</dt>
        <dd>
          {book.rights_confirmation_version
            ? `bevestigd (versie ${book.rights_confirmation_version})`
            : "bevestigd (eerdere versie)"}
        </dd>
        <dt>Herkomstcontrole</dt>
        <dd>
          {findings.length === 0 ? (
            book.provenance.sha256 ? "geen bijzonderheden" : "niet uitgevoerd (ouder boek)"
          ) : (
            <ul className="findings">
              {findings.map((f) => (
                <li key={f.code + (f.detail ?? "")}>
                  <span className={f.severity === "high" ? "danger" : ""}>{f.message}</span>
                </li>
              ))}
            </ul>
          )}
          {book.provenance.acknowledged && (
            <span className="muted small">Waarschuwing gezien en toch doorgegaan.</span>
          )}
        </dd>
        <dt>Publiek domein</dt>
        <dd>
          {pd.status ? (
            <>
              {PD_LABELS[pd.status] ?? pd.status}
              {pd.reasons?.length ? <span className="muted small"> — {pd.reasons.join("; ")}</span> : null}
              <br />
              <span className="muted small">{pd.note}</span>
            </>
          ) : (
            "nog niet gecontroleerd"
          )}{" "}
          <button className="link small" disabled={busy} onClick={() => void checkPublicDomain()}>
            {pd.status ? "opnieuw controleren" : "nu controleren"}
          </button>
        </dd>
        <dt>Boektekst</dt>
        <dd>
          Het bestand wordt na de verwerking verwijderd, de hoofdstuktekst uiterlijk na 24 uur.
          Bewaard blijft alleen wat eruit is afgeleid.
        </dd>
      </dl>
      <h3>Bestand opnieuw aanbieden</h3>
      <p className="muted small">
        Om je wiki opnieuw op te bouwen (bijvoorbeeld na een verbetering van FabulaWiki) is het
        originele bestand nodig, omdat wij het niet bewaren. Bied precies hetzelfde EPUB-bestand
        aan; dat kost geen credit en je eigen bewerkingen worden daarbij overschreven.
      </p>
      <input
        type="file"
        accept=".epub,application/epub+zip"
        disabled={busy || processing}
        onChange={(e) => void reoffer(e.target.files?.[0] ?? null)}
        aria-label="Origineel EPUB-bestand opnieuw aanbieden"
      />
      {msg && <p className="muted">{msg}</p>}
      <ErrorNote error={error} />
    </Card>
  );
}
